'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/frontier'

const GridworldQ = dynamic(() => import('../_components/widgets/FrontierWidgets').then(m => m.GridworldQ), { ssr: false })

export default function ReinforcementLearning() {
  return (
    <Lesson>
      <Step>
        <H>Learning from reward</H>
        <P>
          Supervised learning needs the right answer for every example. But for many problems nobody knows the right move — only
          whether things worked out in the end. A game is won or lost; a robot reaches the door or falls over.{' '}
          <strong>Reinforcement learning</strong> learns from that sparse signal.
        </P>
        <P>
          An <strong>agent</strong> sees a <strong>state</strong>, takes an <strong>action</strong>, and the environment returns
          a new state and a <strong>reward</strong>. The agent’s goal: maximise the total reward over time. It has to discover,
          by trial and error, which actions eventually pay off.
        </P>
      </Step>

      <Step gate="q-explore">
        <H>Q-learning</H>
        <P>
          One classic method keeps a table <M>{'Q(s, a)'}</M>: the expected future reward of taking action <M>a</M> in state{' '}
          <M>s</M> and acting well afterwards. Every time the agent tries something, it nudges that estimate toward what just
          happened plus its estimate of where it ended up:
        </P>
        <MB>{'Q(s,a) \\leftarrow Q(s,a) + \\alpha\\big[\\,r + \\gamma \\max_{a\'} Q(s\', a\') - Q(s,a)\\,\\big]'}</MB>
        <Callout kind="notation">
          <M>\alpha</M> is a learning rate. <M>\gamma</M> (gamma, ~0.95) discounts the future: a reward next step is worth more
          than the same reward ten steps later. <M>{"s'"}</M> is the next state. The bracket is the “surprise” — how wrong the
          old estimate was.
        </Callout>
        <Figure n="10.5" wide caption="An agent learning to cross a maze by Q-learning. Watch value spread backwards from the goal, one square at a time, until every square knows the way.">
          <GridworldQ />
        </Figure>
        <Quiz
          id="q-explore"
          question="What happens if you set exploration ε to 0 from the very start?"
          options={[
            { label: 'The agent always repeats its current best guess and may never discover a better route.', correct: true },
            { label: 'It learns faster, since it never wastes moves.', why: 'Early on, its “best guess” is meaningless — without exploring, it can get stuck.' },
            { label: 'It can’t move at all.', why: 'It moves — it just never tries anything new.' },
          ]}
          explain="The exploration–exploitation trade-off: use what you know, or try something new? Every RL system has to balance it."
        />
      </Step>

      <Step gate="q-learning">
        <H>Implement Q-learning</H>
        <Exercise
          id="q-learning"
          title="Q-learning in a corridor"
          {...EX.qLearning}
          hints={[
            <>
              <C>{'const target = done ? reward : reward + gamma * Math.max(...Q[s2])'}</C>
            </>,
            <>
              <C>Q[s][a] += alpha * (target - Q[s][a])</C>
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Where RL shows up today</H>
        <ul className="gd-list">
          <li>
            <strong>Games:</strong> AlphaGo and its successors learned superhuman play largely from self-play rewards.
          </li>
          <li>
            <strong>Language models:</strong> RLHF (previous lesson) is reinforcement learning with a learned reward.
          </li>
          <li>
            <strong>Reasoning models:</strong> recent models are trained with RL on problems with checkable answers — maths,
            code, puzzles — rewarding chains of thought that reach correct results. It’s a big reason models got much better at
            multi-step reasoning.
          </li>
        </ul>
        <Callout kind="why">
          A table works for a 6×9 maze. For chess, Go or language, the state space is astronomically large, so the Q-table is
          replaced by a neural network — the same networks you’ve been building, trained with gradients pointed by reward.
        </Callout>
      </Step>
    </Lesson>
  )
}
